import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { useState } from 'react';

import { PaymentErrorBoundary } from '../components/payment-error-boundary.js';
import { formatNumber, formatPrice } from '../lib/format.js';
import type { PublicPlan } from '../lib/signup-api.js';

// Module-level singleton; loadStripe tolerates an empty key (returns null
// promise) so render never crashes when the publishable key is unset in
// dev/test.
const stripePromise = loadStripe((import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY ?? '') as string);

interface PaymentStepProps {
  clientSecret: string;
  intentType: 'setup' | 'payment';
  plan: PublicPlan;
  onComplete: () => void;
}

function PaymentInner({
  intentType,
  plan,
  onComplete,
}: {
  intentType: 'setup' | 'payment';
  plan: PublicPlan;
  onComplete: () => void;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handlePay() {
    if (!stripe || !elements || pending) return;
    setPending(true);
    setError(null);
    try {
      const { error: payError } =
        intentType === 'setup'
          ? await stripe.confirmSetup({
              elements,
              confirmParams: { return_url: window.location.href },
              redirect: 'if_required',
            })
          : await stripe.confirmPayment({
              elements,
              confirmParams: { return_url: window.location.href },
              redirect: 'if_required',
            });
      if (payError) {
        setError(payError.message ?? 'Payment could not be completed.');
        return;
      }
      onComplete();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Payment could not be completed.');
    } finally {
      setPending(false);
    }
  }

  return (
    <div className="flex flex-col gap-5">
      <div className="rounded-lg border border-gray-200 p-4">
        <div className="flex items-baseline justify-between gap-4">
          <h2 className="text-base font-semibold text-gray-900">{plan.name}</h2>
          <p className="text-xl font-bold text-gray-900">
            {formatPrice(plan.unitAmount, plan.currency)}
            <span className="text-sm font-normal text-gray-500">/{plan.interval}</span>
          </p>
        </div>

        {plan.trialDays > 0 && (
          <p className="mt-3 text-sm text-gray-600">
            Your card won&apos;t be charged today. Your {formatNumber(plan.trialDays)}-day free
            trial starts now — you&apos;ll be billed {formatPrice(plan.unitAmount, plan.currency)}/
            {plan.interval} after it ends.
          </p>
        )}
      </div>

      <PaymentElement />

      {error && (
        <p role="alert" className="text-sm text-red-600">
          {error}
        </p>
      )}

      <button
        type="button"
        onClick={handlePay}
        disabled={!stripe || pending}
        className="w-full rounded-md bg-brand px-4 py-2.5 text-sm font-semibold text-white hover:bg-brand-dark disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
      >
        {pending
          ? 'Processing…'
          : plan.trialDays > 0
            ? 'Start subscription'
            : `Pay ${formatPrice(plan.unitAmount, plan.currency)}`}
      </button>
    </div>
  );
}

export function PaymentStep({ clientSecret, intentType, plan, onComplete }: PaymentStepProps) {
  return (
    <PaymentErrorBoundary>
      <Elements stripe={stripePromise} options={{ clientSecret }}>
        <PaymentInner intentType={intentType} plan={plan} onComplete={onComplete} />
      </Elements>
    </PaymentErrorBoundary>
  );
}
